import { useCallback, useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getVendorLibraryPreviewFullReport,
  patchVendorLibraryPreviewFullReport,
} from "../api/api-methods";
import { DOCUMENT_LIBRARY_LIST_QUERY_KEY } from "./useDeferredDocumentLibraryRefresh";

export const vendorPreviewFullReportQueryKey = (providerId) => [
  "vendorLibraryPreviewFullReport",
  providerId,
];

export function useVendorPreviewFullReportSetting(providerId, enabled) {
  const queryClient = useQueryClient();
  const canLoad = Boolean(providerId) && Boolean(enabled);
  const query = useQuery({
    queryKey: vendorPreviewFullReportQueryKey(providerId),
    queryFn: () => getVendorLibraryPreviewFullReport(providerId),
    enabled: canLoad,
  });
  const remoteValue = Boolean(query.data?.allow_full_report_preview);
  const [draft, setDraft] = useState(remoteValue);

  useEffect(() => {
    if (query.isSuccess) {
      setDraft(remoteValue);
    }
  }, [query.isSuccess, remoteValue]);

  const mutation = useMutation({
    mutationFn: (allow) => patchVendorLibraryPreviewFullReport(providerId, allow),
    onSuccess: (data) => {
      queryClient.setQueryData(vendorPreviewFullReportQueryKey(providerId), data);
      queryClient.invalidateQueries({ queryKey: [DOCUMENT_LIBRARY_LIST_QUERY_KEY] });
    },
  });

  const reset = useCallback(() => {
    setDraft(remoteValue);
  }, [remoteValue]);

  const save = useCallback(
    () => mutation.mutateAsync(draft),
    [draft, mutation]
  );

  return {
    draft,
    setDraft,
    reset,
    save,
    isReady: query.isSuccess,
    isSaving: mutation.isPending,
  };
}
